import { ChevronDown, ChevronUp, ShieldZap } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { CATEGORY_META } from "./risk-groups";

const CATEGORY_NAME_BY_ID = new Map<string, string>(CATEGORY_META.map((c) => [c.id, c.name]));

/** A risk mapped to a policy, as read from whichever scope the caller owns. */
export type PolicyRiskEntry = { id: string; name: string; category: string };

type RiskEntry = { id: string; name: string; categoryName: string };

/** The risks mapped to a policy. Shared by the merchant screen and the workspace template. */
export const PolicyRisksSidebar = ({ risks: mapped }: { risks: PolicyRiskEntry[] }) => {
  const [open, setOpen] = useState(false);

  // Risks sharing a readable name are collapsed into one entry (as on the risks page).
  const risks = useMemo(() => {
    const byName = new Map<string, RiskEntry>();
    for (const risk of mapped) {
      if (byName.has(risk.name)) continue;
      byName.set(risk.name, {
        id: risk.id,
        name: risk.name,
        categoryName: CATEGORY_NAME_BY_ID.get(risk.category) ?? risk.category,
      });
    }
    return [...byName.values()];
  }, [mapped]);

  const total = risks.length;

  return (
    <div className="bg-[#FCF6F1] border border-border-secondary rounded-md p-5 flex flex-col items-end gap-4">
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-2">
          <ShieldZap aria-hidden="true" className="w-5 h-5 text-primary shrink-0" />
          <p className="text-base font-medium text-primary">
            {total} {total === 1 ? "risk" : "risks"} using this policy
          </p>
        </div>
        <p className="text-sm text-tertiary">Configured to trigger this workflow</p>
      </div>

      <div className="h-px bg-border-secondary w-full" />

      {open && (
        <>
          <div className="flex flex-col gap-3 w-full">
            {risks.map((risk) => (
              <div key={risk.id} className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-tertiary underline decoration-dotted">{risk.name}</span>
                <span className="text-sm text-tertiary shrink-0">{risk.categoryName}</span>
              </div>
            ))}
            {risks.length === 0 && <p className="text-sm text-quaternary italic">No risks mapped yet.</p>}
          </div>

          <div className="h-px bg-border-secondary w-full" />
        </>
      )}

      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-tertiary hover:text-secondary transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "Hide risks" : "See all risks"}
        {open ? (
          <ChevronUp aria-hidden="true" className="w-5 h-5" />
        ) : (
          <ChevronDown aria-hidden="true" className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};
